import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface Ga4PropertyMatch {
  propertyId: string
  displayName: string
  email: string
  domain: string | null
}

export interface Ga4DailyRow {
  date: string
  activeUsers: number
  sessions: number
  pageViews: number
  bounceRate: number
  avgSessionDuration: number
}

export interface Ga4Totals {
  activeUsers: number
  sessions: number
  pageViews: number
  bounceRate: number
  avgSessionDuration: number
}

export interface Ga4ChannelRow {
  channel: string
  sessions: number
  users: number
  pageViews: number
  bounceRate: number
  avgSessionDuration: number
  engagementRate: number
}

export interface Ga4PageRow {
  pagePath: string
  pageViews: number
  users: number
  sessions: number
  bounceRate: number
  avgSessionDuration: number
}

export interface Ga4PageTotals {
  pageViews: number
  users: number
  sessions: number
  bounceRate: number
  avgSessionDuration: number
  engagementRate: number
}

export interface Ga4SourceRow {
  channel: string
  sessions: number
  users: number
}

/** List all GA4 properties across all connected accounts.
 *  Auto-refetches every 5s when backend is still resolving (background discovery).
 */
export function useGa4Properties() {
  return useQuery({
    queryKey: ['ga4', 'properties'],
    queryFn: () => apiClient.get<{ properties: Ga4PropertyMatch[]; resolving?: boolean }>('/api/gsc/ga4/properties'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchInterval: (query) => {
      if (query.state.data?.resolving) return 5_000
      return false
    },
  })
}

/** Check GA4 connection status for a site's domain.
 *  Auto-refetches every 5s when backend is still resolving (background discovery).
 */
export function useGa4Status(siteId: string) {
  return useQuery({
    queryKey: ['ga4', 'status', siteId],
    queryFn: () => apiClient.get<{
      connected: boolean
      properties: Ga4PropertyMatch[]
      resolving?: boolean
    }>(`/api/sites/${siteId}/ga4/status`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
    retry: 1, // Don't retry timeouts aggressively
    refetchInterval: (query) => {
      // Auto-refetch while backend is resolving GA4 property
      if (query.state.data?.resolving) return 5_000
      return false
    },
  })
}

/** Site-level daily performance trends */
export function useGa4Performance(siteId: string, propertyId: string, days = 28) {
  return useQuery({
    queryKey: ['ga4', 'performance', siteId, propertyId, days],
    queryFn: () => apiClient.get<{
      propertyId: string
      dateRange: { startDate: string; endDate: string }
      totals: Ga4Totals
      daily: Ga4DailyRow[]
    }>(`/api/sites/${siteId}/ga4/performance`, {
      params: { propertyId, days },
    }),
    enabled: !!siteId && !!propertyId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Traffic source breakdown */
export function useGa4Acquisition(siteId: string, propertyId: string, days = 28) {
  return useQuery({
    queryKey: ['ga4', 'acquisition', siteId, propertyId, days],
    queryFn: () => apiClient.get<{
      propertyId: string
      dateRange: { startDate: string; endDate: string }
      channels: Ga4ChannelRow[]
    }>(`/api/sites/${siteId}/ga4/acquisition`, {
      params: { propertyId, days },
    }),
    enabled: !!siteId && !!propertyId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Top pages by pageviews */
export function useGa4TopPages(siteId: string, propertyId: string, days = 28, limit = 25) {
  return useQuery({
    queryKey: ['ga4', 'pages', siteId, propertyId, days, limit],
    queryFn: () => apiClient.get<{
      propertyId: string
      dateRange: { startDate: string; endDate: string }
      pages: Ga4PageRow[]
    }>(`/api/sites/${siteId}/ga4/pages`, {
      params: { propertyId, days, limit },
    }),
    enabled: !!siteId && !!propertyId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Per-page GA4 metrics + traffic sources (manual trigger) */
export function useGa4Page(siteId: string, propertyId: string, url: string) {
  return useQuery({
    queryKey: ['ga4', 'page', siteId, propertyId, url],
    queryFn: () => apiClient.get<{
      pagePath: string
      propertyId: string
      dateRange: { startDate: string; endDate: string }
      totals: Ga4PageTotals
      sources: Ga4SourceRow[]
    }>(`/api/sites/${siteId}/ga4/page`, {
      params: { propertyId, url },
    }),
    enabled: false, // Manual trigger via refetch()
    staleTime: 5 * 60 * 1000,
  })
}

/** Invalidate server-side GA4 cache + client queries */
export function useRefreshGa4Cache(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete<{ ok: boolean }>(`/api/sites/${siteId}/ga4/cache`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ga4'] })
    },
  })
}
